import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import { CustomerApi } from '_api';
import { STORAGE_KEYS } from '_constants/storage';
import { handleError, handleResponse } from '_helpers/api';
import { restoreSessionValue, storeSessionValue } from '_helpers/storage';

export const getOrder = createAsyncThunk(
  'order/getOrder',
  async function ({ customerId, orderId }, { dispatch, rejectWithValue }) {
    try {
      const response = await CustomerApi.getOrder({ customerId, orderId });
      return handleResponse(response);
    } catch (error) {
      const processedError = handleError(error);

      if (processedError?.code === 400) {
        dispatch(refreshActiveOrderId(null));
      }
      return rejectWithValue(processedError);
    }
  }
);

export const updateOrder = createAsyncThunk(
  'order/updateOrder',
  async function ({ customerId, orderId, body }, { dispatch, rejectWithValue }) {
    try {
      await CustomerApi.updateOrder({ customerId, orderId, body });
      toast.success('Information was updated successfully!');
      dispatch(getOrder({ customerId, orderId }));
    } catch (error) {
      const processedError = handleError(error);
      return rejectWithValue(processedError);
    }
  }
);

export const updateProduct = createAsyncThunk(
  'order/updateProduct',
  async function ({ customerId, orderId, productId, body }, { dispatch, rejectWithValue }) {
    try {
      await CustomerApi.updateProduct({ customerId, productId, body });
      toast.success('Information was updated successfully!');
      dispatch(getOrder({ customerId, orderId }));
    } catch (error) {
      const processedError = handleError(error);
      return rejectWithValue(processedError);
    }
  }
);

const orderSlice = createSlice({
  name: 'order',
  initialState: {
    activeOrderId: null,
    data: {},
    error: null,
    loading: false,
    orderUpdating: false,
    productUpdating: false,
  },
  reducers: {
    refreshActiveOrderId(state, action) {
      storeSessionValue(STORAGE_KEYS.activeOrderId, action.payload);
      state.activeOrderId = action.payload;
    },
    resetActiveOrderId(state) {
      state.activeOrderId = null;
    },
    restoreActiveOrderId(state) {
      const restoredActiveOrderId = restoreSessionValue(STORAGE_KEYS.activeOrderId);

      if (restoredActiveOrderId) {
        state.activeOrderId = restoredActiveOrderId;
      }
    },
    resetData(state) {
      state.data = {};
    },
    resetError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getOrder.pending, (state) => {
        state.error = null;
        state.loading = true;
      })
      .addCase(getOrder.fulfilled, (state, action) => {
        state.data = action.payload;
        state.loading = false;
      })
      .addCase(getOrder.rejected, (state, action) => {
        state.error = action.payload;
        state.loading = false;
      });

    builder
      .addCase(updateOrder.pending, (state) => {
        state.orderUpdating = true;
      })
      .addCase(updateOrder.fulfilled, (state) => {
        state.orderUpdating = false;
      })
      .addCase(updateOrder.rejected, (state) => {
        state.orderUpdating = false;
      });

    builder
      .addCase(updateProduct.pending, (state) => {
        state.productUpdating = true;
      })
      .addCase(updateProduct.fulfilled, (state) => {
        state.productUpdating = false;
      })
      .addCase(updateProduct.rejected, (state) => {
        state.productUpdating = false;
      });
  },
});

export const { refreshActiveOrderId, resetActiveOrderId, restoreActiveOrderId, resetData, resetError } =
  orderSlice.actions;

export default orderSlice.reducer;
